import { apiClient } from './client';

/**
 * 휴일대체(보상휴가) — 근기법 §57 (2026-09-06 · 판 B).
 *
 * 서버 SSOT: `apps/chat-server/src/modules/hr/substitute.routes.ts`.
 *
 * ★★★ **화면은 「몇 분이 쌓이는지」를 보내지 않는다.** 보내는 것은 «어느 날 · 왜» 뿐이고
 *    적립 분은 **서버가 승인된 연장 기록에서 다시 센다** — 화면이 보낸 숫자를 믿으면
 *    그 숫자가 곧 휴가가 된다. 후보의 `creditMinutes` 는 **미리보기**다.
 * ★★ **꺼진 회사는 후보가 빈 목록이다**(400 이 아니다) — 지금 모든 회사가 그 상태이므로
 *    화면은 정책을 먼저 읽어 **입구를 세울지**부터 정한다.
 */

export type SubstituteExpireRule = 'YEAR_END_NEXT' | 'MONTHS';

export interface SubstitutePolicyDto {
  enabled: boolean;
  allowOvertime: boolean;
  allowNight: boolean;
  allowHoliday: boolean;
  expireRule: SubstituteExpireRule;
  expireMonths: number;
  agreementNote: string | null;
}

/** 바꿀 수 있는 하루 — 원천 실측(갈래별)과 **바뀔 시간**(가산 포함)이 함께 온다. */
export interface SubstituteCandidateDto {
  date: string;
  overtimeMinutes: number;
  holidayMinutes: number;
  holidayOverMinutes: number;
  nightMinutes: number;
  creditMinutes: number;
  creditText: string;
}

export interface SubstituteCandidatesDto {
  month: string;
  policy: SubstitutePolicyDto;
  items: SubstituteCandidateDto[];
}

export interface SubstituteCreditDto {
  id: string;
  workDate: string;
  creditMinutes: number;
  creditText: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELED';
  expiresAt?: string | null;
  documentId?: string | null;
  reason?: string | null;
  overtimeMinutes?: number;
  holidayMinutes?: number;
  holidayOverMinutes?: number;
  nightMinutes?: number;
  // ── 판 C (2026-09-06). 옛 서버는 안 준다. ──
  /**
   * 이 줄에 **남은** 분. `null` 이면 «모른다» 다 — 승인 아닌 줄은 배분에 안 들어간다.
   * ★ 0 으로 그리면 「다 썼다」로 읽힌다. 셋(모른다 · 0 · 아직)은 다 다른 말이다.
   */
  leftMinutes?: number | null;
  leftText?: string | null;
  /** 남았는데 기한이 지났나 — **임금으로 돌려받을** 몫이다. */
  expired?: boolean;
}

/** 신청이 결재로 올라간 결과 — 휴가·출장과 **같은 모양**이다. */
export interface SubstituteSubmitDto {
  documentId: string | null;
  blocked: boolean;
  reason: string | null;
}

export async function getSubstitutePolicy(): Promise<SubstitutePolicyDto> {
  const { data } = await apiClient.get<{ policy: SubstitutePolicyDto }>('/hr/substitute/policy');
  return data.policy;
}

export async function patchSubstitutePolicy(patch: Partial<SubstitutePolicyDto>): Promise<SubstitutePolicyDto> {
  const { data } = await apiClient.patch<{ policy: SubstitutePolicyDto }>('/hr/substitute/policy', patch);
  return data.policy;
}

/** `month` 는 `YYYY-MM`. 안 주면 이번 달. */
export async function listSubstituteCandidates(month?: string): Promise<SubstituteCandidatesDto> {
  const { data } = await apiClient.get<SubstituteCandidatesDto>('/hr/substitute/candidates', {
    params: month ? { month } : undefined,
  });
  return data;
}

export interface CreateSubstituteInput {
  workDate: string;
  reason: string;
  /**
   * 기안자가 고른 결재선 — **있을 때만 싣는다.**
   * ★ 옛 서버는 이 칸을 모르고 `additionalProperties:false` 라 **조용히 지운다**.
   *   안 실으면 서버가 지금까지처럼 조직에서 뽑는다.
   */
  approverIds?: string[];
  referrerIds?: string[];
}

export async function createSubstitute(
  input: CreateSubstituteInput,
): Promise<{ credit: SubstituteCreditDto; approval?: SubstituteSubmitDto }> {
  const { data } = await apiClient.post<{ credit: SubstituteCreditDto; approval?: SubstituteSubmitDto }>(
    '/hr/substitute', input,
  );
  return data;
}

/**
 * 내 적립 — 판 C 에서 **줄마다 「얼마 남았나 · 지났나」**가 함께 온다.
 * ★ 옛 서버는 그 칸을 안 준다(`leftMinutes: undefined`) — 화면은 그때 잔량 칸을 안 그린다.
 */
export interface MySubstituteDto {
  items: SubstituteCreditDto[];
  creditedMinutes: number;
  liveMinutes?: number;
  expiredMinutes?: number;
  /** 원장에서도 조정분에서도 못 뺀 사용분 — **0 으로 삼키지 않는다**. */
  unmatchedMinutes?: number;
  dailyMinutes?: number;
}

export async function listMySubstituteCredits(): Promise<MySubstituteDto> {
  const { data } = await apiClient.get<MySubstituteDto>('/hr/substitute/me');
  return { ...data, items: data.items ?? [], creditedMinutes: data.creditedMinutes ?? 0 };
}

/**
 * 기한이 지나 못 쓴 몫 — **임금으로 돌려줘야 하는 돈**(§57) · 판 C.
 *
 * ★ 담당자만. `amount` 가 `null` 이면 **0 원이 아니라 「임금 정보 없음」** 이다.
 */
export interface SubstituteExpiredRowDto {
  id: string;
  workDate: string;
  expiresAt: string | null;
  leftMinutes: number;
  leftText: string;
}

export interface SubstituteSettlementRowDto {
  userId: string;
  name: string;
  employeeNumber: string | null;
  expiredMinutes: number;
  expiredText: string;
  liveMinutes: number;
  hourlyWage: number | null;
  amount: number | null;
  expiredRows: SubstituteExpiredRowDto[];
}

export interface SubstituteSettlementDto {
  asOf: string;
  policy: SubstitutePolicyDto;
  rows: SubstituteSettlementRowDto[];
  total: { people: number; expiredMinutes: number; amount: number; unknownWage: number };
}

export async function getSubstituteSettlement(asOf?: string): Promise<SubstituteSettlementDto> {
  const { data } = await apiClient.get<SubstituteSettlementDto>('/hr/substitute/settlement', {
    params: asOf ? { asOf } : undefined,
  });
  return { ...data, rows: Array.isArray(data?.rows) ? data.rows : [] };
}
